/**
 * D4F COLLECTION FILTERS + VIEW SWITCHER
 * Datei: assets/d4f-collection-filters.js
 *
 * - Alle Produkte werden aus JSON-Block gelesen (seitenübergreifend)
 * - Clientseitiges Filtern via JSON-Daten (kein Ajax, kein DOM-Scraping)
 * - Filter: Anlass (events) + Für wen (persons)
 * - Suche nach Produkttitel
 * - Sortierung
 * - Clientseitige Pagination
 * - View Toggle: Desktop 2/3/4/list – Mobil 1/2/list
 * - View wird in localStorage gespeichert
 */
(function () {
  'use strict';

  const MOBILE_BP = 900;
  const STORE_KEY = 'd4f-collection-view';

  // ── Refs ────────────────────────────────────────────────────────────────
  const section = document.querySelector('.d4f-collection-filters');
  if (!section) return;

  const productGrid      = document.getElementById('d4f-product-grid');
  const noResults        = document.getElementById('d4f-no-results');
  const resultsNumber    = document.getElementById('d4f-results-number');
  const searchInput      = document.getElementById('d4f-product-search');
  const searchClear      = section.querySelector('.d4f-search-clear');
  const sortSelect       = document.getElementById('d4f-sort-select');
  const clearFiltersBtn  = document.getElementById('d4f-clear-filters');
  const activeFiltersBox = document.getElementById('d4f-active-filters');
  const activeFiltersTags= document.getElementById('d4f-active-filters-tags');
  const filterBadges     = section.querySelectorAll('.d4f-filter-badge');
  const desktopViewBtns  = section.querySelectorAll('.d4f-view-btn.d4f-view-btn--desktop');
  const mobileViewBtns   = section.querySelectorAll('.d4f-view-btn.d4f-view-btn--mobile');
  const paginationNav    = document.getElementById('d4f-pagination');

  // ── Alle Produkte aus JSON-Block laden ──────────────────────────────────
  const jsonEl = document.getElementById('d4f-all-products-json');
  if (!jsonEl || !productGrid) return;

  let allProducts = [];
  try {
    allProducts = JSON.parse(jsonEl.textContent);
  } catch (e) {
    console.error('D4F: Fehler beim Parsen der Produkt-JSON-Daten', e);
    return;
  }

  // Produkte pro Seite aus data-Attribut der Listing-Section lesen
  const listingSection  = document.getElementById(productGrid.closest('.d4f-collection-listing')?.id || '');
  const productsPerPage = parseInt(
    document.querySelector('.d4f-collection-listing')?.dataset.productsPerPage || '24',
    10
  );

  // ── State ────────────────────────────────────────────────────────────────
  const state = {
    search:      '',
    sortBy:      'manual',
    events:      [],
    persons:     [],
    currentPage: 1
  };

  // ── View Switch ───────────────────────────────────────────────────────────
  function isMobile() {
    return window.innerWidth <= MOBILE_BP;
  }

  function getDefaultView() {
    const s = document.querySelector('.d4f-collection-filters');
    return isMobile()
      ? (s?.dataset.defaultViewMobile || 'grid-2')
      : (s?.dataset.defaultView       || 'grid-3');
  }

  function applyView(view) {
    if (!productGrid) return;
    productGrid.className = productGrid.className
      .replace(/d4f-product-grid--\S+/g, '')
      .trim();
    productGrid.classList.add('d4f-product-grid--' + view);

    const relevantBtns = isMobile() ? mobileViewBtns : desktopViewBtns;
    relevantBtns.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.view === view);
    });

    try { localStorage.setItem(STORE_KEY, view); } catch (e) {}
  }

  function initView() {
    let saved = null;
    try { saved = localStorage.getItem(STORE_KEY); } catch (e) {}
    applyView(saved || getDefaultView());
  }

  desktopViewBtns.forEach(btn => btn.addEventListener('click', () => applyView(btn.dataset.view)));
  mobileViewBtns.forEach(btn  => btn.addEventListener('click', () => applyView(btn.dataset.view)));

  window.addEventListener('resize', debounce(() => {
    let saved = null;
    try { saved = localStorage.getItem(STORE_KEY); } catch (e) {}
    applyView(saved || getDefaultView());
  }, 250));

  // ── Suche ─────────────────────────────────────────────────────────────────
  searchInput?.addEventListener('input', debounce(e => {
    state.search      = e.target.value.toLowerCase().trim();
    state.currentPage = 1;
    applyFilters();
  }, 250));

  searchClear?.addEventListener('click', () => {
    if (searchInput) searchInput.value = '';
    state.search      = '';
    state.currentPage = 1;
    applyFilters();
  });

  // ── Sortierung ────────────────────────────────────────────────────────────
  sortSelect?.addEventListener('change', e => {
    state.sortBy      = e.target.value;
    state.currentPage = 1;
    applyFilters();
  });

  // ── Badge Filter ──────────────────────────────────────────────────────────
  filterBadges.forEach(badge => {
    badge.addEventListener('click', () => {
      const type  = badge.dataset.filterType;
      const value = badge.dataset.filterValue;
      const arr   = type === 'event' ? state.events : state.persons;
      const idx   = arr.indexOf(value);

      if (idx > -1) {
        arr.splice(idx, 1);
        badge.classList.remove('is-active');
      } else {
        arr.push(value);
        badge.classList.add('is-active');
      }

      state.currentPage = 1;
      applyFilters();
      renderActiveTags();
      updateClearBtn();
    });
  });

  clearFiltersBtn?.addEventListener('click', () => {
    state.events      = [];
    state.persons     = [];
    state.currentPage = 1;
    filterBadges.forEach(b => b.classList.remove('is-active'));
    applyFilters();
    renderActiveTags();
    updateClearBtn();
  });

  // ── Filter + Render ───────────────────────────────────────────────────────
  function applyFilters() {
    // 1. Filtern
    let filtered = allProducts.filter(p => matchesFilter(p));

    // 2. Sortieren
    filtered = sortProducts(filtered);

    // 3. Ergebnisanzahl
    if (resultsNumber) resultsNumber.textContent = filtered.length;

    // 4. Pagination berechnen
    const totalPages = Math.ceil(filtered.length / productsPerPage);
    if (state.currentPage > totalPages) state.currentPage = Math.max(1, totalPages);

    const start   = (state.currentPage - 1) * productsPerPage;
    const pageProducts = filtered.slice(start, start + productsPerPage);

    // 5. Karten rendern
    productGrid.innerHTML = '';
    pageProducts.forEach((p, idx) => {
      productGrid.appendChild(buildCard(p, start + idx));
    });

    // 6. No-Results
    if (noResults) {
      noResults.style.display = filtered.length === 0 ? 'block' : 'none';
    }

    // 7. Pagination rendern
    renderPagination(totalPages);
  }

  function matchesFilter(p) {
    if (state.search && !p.titleLower.includes(state.search)) return false;

    if (state.events.length > 0) {
      const match = state.events.some(f => p.events.map(e => e.trim()).includes(f));
      if (!match) return false;
    }

    if (state.persons.length > 0) {
      const match = state.persons.some(f => p.persons.map(e => e.trim()).includes(f));
      if (!match) return false;
    }

    return true;
  }

  function sortProducts(products) {
    const arr = [...products];
    switch (state.sortBy) {
      case 'title-ascending':
        arr.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-descending':
        arr.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'price-ascending':
        arr.sort((a, b) => a.price - b.price);
        break;
      case 'price-descending':
        arr.sort((a, b) => b.price - a.price);
        break;
      case 'created-descending':
        arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        break; // manual/best-selling → Shopify-Reihenfolge (JSON-Reihenfolge)
    }
    return arr;
  }

  // ── Karten bauen ──────────────────────────────────────────────────────────
  function buildCard(p, globalIdx) {
    const a = document.createElement('a');
    a.href      = p.url;
    a.className = 'd4f-product-card';
    a.dataset.productTitle   = p.titleLower;
    a.dataset.productPrice   = p.price;
    a.dataset.productCreated = p.createdAt;
    a.dataset.productEvents  = p.events.join(',');
    a.dataset.productPersons = p.persons.join(',');

    // Badges
    let badgesHtml = '';
    if (p.hasDiscount)  badgesHtml += `<span class="d4f-badge d4f-badge--sale">-${p.discountPct}%</span>`;
    if (p.isNew)        badgesHtml += `<span class="d4f-badge d4f-badge--new">NEU</span>`;
    if (p.isTopseller)  badgesHtml += `<span class="d4f-badge d4f-badge--topseller">⭐ Topseller</span>`;
    if (p.isPopular)    badgesHtml += `<span class="d4f-badge d4f-badge--popular">🔥 Häufig gekauft</span>`;

    // Bild
    let imgHtml = '';
    if (p.image.exists) {
      const loading = globalIdx < 4 ? 'eager' : 'lazy';
      imgHtml = `
        <img
          src="${escHtml(p.image.src600)}"
          srcset="${escHtml(p.image.src400)} 400w, ${escHtml(p.image.src600)} 600w, ${escHtml(p.image.src900)} 900w"
          sizes="(max-width: 480px) 50vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt="${escHtml(p.image.alt)}"
          loading="${loading}"
          class="d4f-product-card__image"
          width="${p.image.width}"
          height="${p.image.height}"
        >`;
    } else {
      imgHtml = `
        <div class="d4f-product-card__no-image">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <rect width="48" height="48" rx="8" fill="rgba(212,165,116,0.1)"/>
            <path d="M12 36l8-10 6 7 4-5 8 8H12z" fill="rgba(212,165,116,0.3)"/>
            <circle cx="18" cy="20" r="3" fill="rgba(212,165,116,0.3)"/>
          </svg>
        </div>`;
    }

    // Preise
    let pricingHtml = `<span class="d4f-product-card__price">${escHtml(p.priceFormatted)}</span>`;
    if (p.hasDiscount) {
      pricingHtml += `
        <span class="d4f-product-card__compare-price">${escHtml(p.comparePriceFormatted)}</span>
        <span class="d4f-product-card__discount">-${p.discountPct}%</span>`;
    }

    // Kurzbeschreibung
    const excerptHtml = p.shortDesc
      ? `<p class="d4f-product-card__excerpt">${escHtml(p.shortDesc)}</p>`
      : '';

    a.innerHTML = `
      <div class="d4f-product-card__image-wrap">
        <div class="d4f-product-card__badges">${badgesHtml}</div>
        ${imgHtml}
      </div>
      <div class="d4f-product-card__body">
        <h3 class="d4f-product-card__title">${escHtml(p.title)}</h3>
        ${excerptHtml}
        <div class="d4f-product-card__pricing">${pricingHtml}</div>
      </div>`;

    return a;
  }

  // ── Clientseitige Pagination ──────────────────────────────────────────────
  function renderPagination(totalPages) {
    if (!paginationNav) return;

    if (totalPages <= 1) {
      paginationNav.style.display = 'none';
      paginationNav.innerHTML     = '';
      return;
    }

    paginationNav.style.display = 'flex';
    const cur = state.currentPage;
    let html  = '';

    // Zurück-Button
    if (cur > 1) {
      html += `<button class="d4f-pagination__item" data-page="${cur - 1}" aria-label="Vorherige Seite">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>`;
    }

    // Seitenzahlen mit Ellipsis
    const pages = buildPageRange(cur, totalPages);
    pages.forEach(p => {
      if (p === '...') {
        html += `<span class="d4f-pagination__item d4f-pagination__item--dots">…</span>`;
      } else if (p === cur) {
        html += `<span class="d4f-pagination__item d4f-pagination__item--active">${p}</span>`;
      } else {
        html += `<button class="d4f-pagination__item" data-page="${p}">${p}</button>`;
      }
    });

    // Weiter-Button
    if (cur < totalPages) {
      html += `<button class="d4f-pagination__item" data-page="${cur + 1}" aria-label="Nächste Seite">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>`;
    }

    paginationNav.innerHTML = html;

    paginationNav.querySelectorAll('[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        state.currentPage = parseInt(btn.dataset.page, 10);
        applyFilters();
        // Sanft nach oben scrollen zum Grid
        productGrid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function buildPageRange(cur, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = [];
    pages.push(1);

    if (cur > 3)          pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < total - 2)  pages.push('...');

    pages.push(total);
    return pages;
  }

  // ── Aktive Filter Tags ────────────────────────────────────────────────────
  function renderActiveTags() {
    if (!activeFiltersTags || !activeFiltersBox) return;

    activeFiltersTags.innerHTML = '';

    const allActive = [
      ...state.events.map(v  => ({ type: 'event',  value: v })),
      ...state.persons.map(v => ({ type: 'person', value: v }))
    ];

    if (allActive.length === 0) {
      activeFiltersBox.style.display = 'none';
      return;
    }

    activeFiltersBox.style.display = 'block';

    allActive.forEach(({ type, value }) => {
      const tag = document.createElement('span');
      tag.className = 'd4f-active-filter-tag';
      tag.innerHTML = `<span>${escHtml(value)}</span><span class="d4f-active-filter-tag__remove">×</span>`;
      tag.addEventListener('click', () => {
        const badge = Array.from(filterBadges).find(
          b => b.dataset.filterType === type && b.dataset.filterValue === value
        );
        badge?.click();
      });
      activeFiltersTags.appendChild(tag);
    });
  }

  function updateClearBtn() {
    if (!clearFiltersBtn) return;
    const hasActive = state.events.length > 0 || state.persons.length > 0;
    clearFiltersBtn.style.display = hasActive ? 'inline-block' : 'none';
  }

  // ── Hilfsfunktionen ───────────────────────────────────────────────────────
  function escHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  initView();
  applyFilters(); // Erstes Render aller Karten

})();